import React, { Component, Suspense } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import { connect } from "react-redux";
import { auth, teamMembers as teamMembersActions } from "./actions";

import Preloader from "./components/generics/preloader";

import FormSetting from "./components/Setting/FormSetting";
import TextSetting from "./components/Setting/TextSetting";
import ImageSetting from "./components/Setting/ImageSetting";
import DeviceSetting from "./components/Setting/DeviceSetting";
import DashboardSetting from "./components/Setting/DashboardSetting";
import ForgotPassword from "./components/Login/ForgotPassword";
import ChangePassword from "./components/Login/ChangePassword";

const InstagramOAuthCallback = React.lazy(() =>
  import("./components/OAuth/Instagram/index")
);
const AddGoogleLocation = React.lazy(() =>
  import("./components/OAuth/Google/AddGoogleLocation")
);
const TwitterOAuthCallback = React.lazy(() =>
  import("./components/OAuth/Twitter/index")
);
const TicketPortal = React.lazy(() => import("./components/TicketPortal"));
const NotFound = React.lazy(() => import("./components/generics/notfound"));
const Index = React.lazy(() => import("./components"));
const ProxyLogin = React.lazy(() => import("./components/Login"));
const Login = React.lazy(() => import("./components/Login/Login"));
const Register = React.lazy(() => import("./components/Login/Register"));
const DashboardV2 = React.lazy(() => import("./components/Dashboard/DashboardV2"));
const Dashboard = React.lazy(() => import("./components/Dashboard"));
const TicketResolutionPage = React.lazy(() => import("./components/ReportPage/TicketResolutionPage"));
const Employee = React.lazy(() => import("./components/Employee"));
const FeedbackForms = React.lazy(() =>
  import("./components/FeedbackForms/index")
);
const FeedbackFormsDetails = React.lazy(() =>
  import("./components/FeedbackForms/FeedbackFormDetail/")
);
const Response = React.lazy(() => import("./components/Response"));
const Templates = React.lazy(() => import("./components/Templates"));
const TemplateDetail = React.lazy(() =>
  import("./components/Templates/TemplateDetail")
);
const Customers = React.lazy(() => import("./components/Customers"));
const CustomerDetails = React.lazy(() =>
  import("./components/Customers/CustomerDetails")
);
const UploadCustomers = React.lazy(() =>
  import("./components/Customers/Upload")
);
const EmailCampaign = React.lazy(() =>
  import("./components/Customers/Campaign/Email")
);
const CreateEmailCampaign = React.lazy(() =>
  import("./components/Customers/Campaign/Email/CreateEmailCampaign")
);
const EmailSettings = React.lazy(() =>
  import("./components/Customers/Campaign/Email/Settings")
);
const BalanceHistory = React.lazy(() => import("./components/BalanceHistory"));
const UnsubscribeView = React.lazy(() =>
  import("./components/Customers/UnsubscribeView")
);
const CampaignReport = React.lazy(() =>
  import("./components/Reports/Campaign")
);
const DetailedReport = React.lazy(() =>
  import("./components/Reports/Campaign/DetailedReport")
);

const Feedbacks = React.lazy(() => import("./components/Feedbacks"));
const FeedbackDetails = React.lazy(() =>
  import("./components/TicketManagement/FeedbackDetails")
);

const TicketManagement = React.lazy(() =>
  import("./components/TicketManagement")
);
const TicketDetails = FeedbackDetails;

const Reviews = React.lazy(() => import("./components/Reviews"));
const Members = React.lazy(() => import("./components/Members"));
const AddMember = React.lazy(() =>
  import("./components/Members/AddMembersForm/index")
);
const ReedemCoupon = React.lazy(() =>
  import("./components/Members/ReedemCoupon")
);
//import member details
const MemberDetails = React.lazy(() =>
  import("./components/Members/MemberDetails")
);

const Preview = React.lazy(() => import("./components/Preview"));
const HospitalFeedbackTemplate = React.lazy(() =>
  import("./components/HospitalFeedbackTemplate")
);

const EditMemberDetails = React.lazy(() =>
  import("./components/Members/MemberDetails/EditDetails/index")
);

// Report Routes

const ReportPage = React.lazy(() => import("./components/ReportPage"));
const FullComparisonPage = React.lazy(() => import("./components/Comparison"));
const GenerateReport = React.lazy(() =>
  import("./components/ReportPage/GenerateReport")
);

const DetailedGenerateReport = React.lazy(() =>
  import("./components/ReportPage/GenerateReport/DetailedReport/")
);

const VoucherUpload = React.lazy(() => import("./components/VoucherUpload"));


// Settings Routes

const SettingPage = React.lazy(() => import("./components/Setting"));
const TeamMembers = React.lazy(() => import("./components/TeamMembers"));

// Subscription Routes

const SubscriptionPage = React.lazy(() => import("./components/Subscription"));

// const ReviewReport = React.lazy(() => import('./components/Reviews/ReviewsList/index'))

const AccountsManage = React.lazy(() =>
  import("./components/Reviews/AccountsManage")
);
const ReviewVerification = React.lazy(() =>
  import("./components/Reviews/ReviewVerification")
);
const MetaLeads = React.lazy(() => import("./components/MetaLeads"));
const LoginInfo = React.lazy(() => import("./components/LoginInfo"));

class Routes extends Component {
  hasRequestedUserDetails = false;
  hasInitializedAuthenticatedSession = false;

  requestUserDetails = () => {
    if (this.hasRequestedUserDetails) {
      return false;
    }

    this.hasRequestedUserDetails = true;
    this.props.loadUser();
    return true;
  };

  initializeAuthenticatedSession = () => {
    if (this.hasInitializedAuthenticatedSession) {
      return;
    }

    this.hasInitializedAuthenticatedSession = true;
    this.props.fetchMyTeamPermissions();
    this.props.fetchStoreConfig();
  };

  componentDidMount() {
    if (!this.props.auth.isAuthenticated || this.needsUserDetails()) {
      this.requestUserDetails();
    } else {
      this.initializeAuthenticatedSession();
    }
  }

  needsUserDetails = () => {
    const user = this.props.auth.user;
    return !!(
      this.props.auth.isAuthenticated
      && (!user || typeof user.is_superuser === "undefined")
    );
  };

  componentDidUpdate(prevProps) {
    if (!this.props.auth.isAuthenticated) {
      this.hasRequestedUserDetails = false;
      this.hasInitializedAuthenticatedSession = false;
      return;
    }

    if (
      this.needsUserDetails()
      && !this.props.auth.isLoading
      && prevProps.auth.user !== this.props.auth.user
    ) {
      if (this.requestUserDetails()) {
        return;
      }
    }
    this.initializeAuthenticatedSession();
  }

  // Restricted team members only get through when they hold the matching
  // permission flag. Parent/owner accounts (is_team_member: false) always pass.
  hasScreenAccess = (requiredPermission) => {
    const perms = this.props.myTeamPermissions;
    if (!requiredPermission || !perms || !perms.is_team_member) {
      return true;
    }
    return perms.active && !!perms.permissions[requiredPermission];
  };

  PrivateRoute = ({ component: ChildComponent, requiredPermission, parentOnly, superuserOnly, staffOnly, ...rest }) => {
    return (
      <Route
        {...rest}
        render={(props) => {
          const perms = this.props.myTeamPermissions;
          if (this.props.auth.isLoading) {
            return <em>Loading...</em>;
          } else if (!this.props.auth.isAuthenticated) {
            return <Redirect to="/login/" />;
          } else if (parentOnly && perms && perms.is_team_member) {
            // Team members manage their own screens, not the team roster itself.
            return <Redirect to="/dashboard/" />;
          } else if (superuserOnly && (!this.props.auth.user || typeof this.props.auth.user.is_superuser === "undefined")) {
            // On a hard refresh the token is restored before the user profile.
            // Wait for loadUser instead of redirecting an authorized owner away.
            return <em>Loading...</em>;
          } else if (superuserOnly && !this.props.auth.user.is_superuser) {
            return <Redirect to="/dashboard/" />;
          } else if (staffOnly && (!this.props.auth.user || typeof this.props.auth.user.is_staff === "undefined")) {
            return <em>Loading...</em>;
          } else if (staffOnly && !this.props.auth.user.is_staff) {
            return <Redirect to="/dashboard/" />;
          } else if (!this.hasScreenAccess(requiredPermission)) {
            return <Redirect to="/dashboard/" />;
          } else {
            return <ChildComponent {...props} />;
          }
        }}
      />
    );
  };

  render() {
    let PrivateRoute = this.PrivateRoute;

    return (
      <Router>
        <Suspense fallback={<Preloader />}>
          <Switch>
            <Route path="/ticket-portal/:kind(employee|customer)/:token/" component={TicketPortal} exact />
            <Route path="/" component={Index} exact />
            <Route path="/redirect/login/" component={ProxyLogin} exact />
            <Route path="/login/" component={Login} exact />
            <Route path="/forgot-password/" component={ForgotPassword} exact />
            <Route
              path="/password-reset/:token"
              component={ChangePassword}
              exact
            />
            <Route path="/register/" component={Register} exact />

            <Route
              path="/google/locations/"
              component={AddGoogleLocation}
              exact
            />

            <Route
              path="/instagram/callback"
              component={InstagramOAuthCallback}
              exact
            />

            <Route
              path="/twitter/callback"
              component={TwitterOAuthCallback}
              exact
            />

            <Route path="/unsub/:secret/" component={UnsubscribeView} exact />
            <Route path="/response/:slug/" component={Response} exact />
            <Route path="/response/:slug/:fid" component={Response} exact />
            <Route
              path="/hospital-feedback-template/"
              component={HospitalFeedbackTemplate}
              exact
            />
            <Route
              path="/hospital-feedback-template/:template/"
              component={HospitalFeedbackTemplate}
              exact
            />

            <Route
              path="/feedback-form-templates/"
              component={Templates}
              exact
            />

            <Route
              path="/c/:slug-feedback-form-templates/"
              render={({ match }) => (
                <Redirect
                  to={`/feedback-form-templates/c/${match.params["slug"]}-feedback-form-templates/`}
                />
              )}
              exact
            />
            <Route
              path="/feedback-form-templates/c/:slug-feedback-form-templates/"
              component={Templates}
              exact
            />
            <Route
              path="/feedback-form-templates/:slug/"
              component={TemplateDetail}
              exact
            />
            <Route
              path="/feedback-form-templates/:slug/preview/:id"
              component={Preview}
              exact
            />

            <PrivateRoute
              path="/feedback-forms/"
              component={FeedbackForms}
              exact
            />
            <PrivateRoute
              path="/feedback-forms/templates/"
              component={FeedbackForms}
              exact
            />
            <PrivateRoute
              path="/feedback-forms/create/"
              component={FeedbackFormsDetails}
              exact
            />
            <PrivateRoute
              path="/feedback-forms/:formID/"
              component={FeedbackFormsDetails}
              exact
            />

            <PrivateRoute path="/dashboard/" component={DashboardV2} exact />
            <PrivateRoute path="/dashboard-v2/" component={Dashboard} exact />
            <PrivateRoute path="/employee/" component={Employee} exact />
            <PrivateRoute
              path="/report-page/"
              component={ReportPage}
              requiredPermission="can_access_reports"
              exact
            />
            <PrivateRoute
              path="/full-comparison/"
              component={FullComparisonPage}
              requiredPermission="can_access_comparison"
              exact
            />
            <PrivateRoute
              path="/full-details/"
              component={FullComparisonPage}
              requiredPermission="can_access_reports"
              exact
            />
            <PrivateRoute
              path="/ticket-resolution/"
              component={TicketResolutionPage}
              requiredPermission="can_access_reports"
              exact
            />
            <PrivateRoute
              path="/comparison/"
              component={FullComparisonPage}
              requiredPermission="can_access_comparison"
              exact
            />
            <PrivateRoute
              path="/generate-report/:slug/"
              component={GenerateReport}
              requiredPermission="can_access_reports"
              exact
            />

            <PrivateRoute
              path="/report/detailed-report/"
              component={DetailedGenerateReport}
              requiredPermission="can_access_reports"
              exact
            />

            {/* Settings */}
            <PrivateRoute path="/settings" component={SettingPage} exact />
            <PrivateRoute
              path="/setting/form-setting/"
              component={FormSetting}
              exact
            />
            <PrivateRoute
              path="/setting/text-setting/"
              component={TextSetting}
              exact
            />
            <PrivateRoute
              path="/setting/image-setting/"
              component={ImageSetting}
              exact
            />
            <PrivateRoute
              path="/setting/device-setting/"
              component={DeviceSetting}
              exact
            />
            <PrivateRoute
              path="/setting/dashboard-setting/"
              component={DashboardSetting}
              exact
            />
            <PrivateRoute
              path="/setting/team-members/"
              component={TeamMembers}
              parentOnly
              exact
            />

            {/* Subscriptions */}
            <PrivateRoute
              path="/subscriptions"
              component={SubscriptionPage}
              exact
            />

            <PrivateRoute path="/customers/" component={Customers} exact />
            <PrivateRoute
              path="/customers/d/:id/"
              component={CustomerDetails}
              exact
            />
            <PrivateRoute
              path="/customers/upload/"
              component={UploadCustomers}
              exact
            />
            <PrivateRoute
              path="/customers/campaigns/email/"
              component={EmailCampaign}
              exact
            />
            <PrivateRoute
              path="/customers/campaigns/email/:id/"
              component={CreateEmailCampaign}
              exact
            />
            <PrivateRoute
              path="/customers/campaigns/email-settings/"
              component={EmailSettings}
              exact
            />

            <PrivateRoute
              path="/balance-history/"
              component={BalanceHistory}
              exact
            />

            <PrivateRoute
              path="/report/campaign/"
              component={CampaignReport}
              exact
            />
            <PrivateRoute
              path="/report/campaign/:id/"
              component={DetailedReport}
              exact
            />

            <PrivateRoute
              path="/feedbacks/"
              component={Feedbacks}
              requiredPermission="can_access_feedbacks"
              exact
            />
            <PrivateRoute
              path="/feedbacks/:id/"
              component={FeedbackDetails}
              requiredPermission="can_access_feedbacks"
              exact
            />

            <PrivateRoute
              path="/tickets/"
              component={TicketManagement}
              requiredPermission="can_access_tickets"
              exact
            />
            <PrivateRoute
              path="/tickets/:id/"
              component={TicketDetails}
              requiredPermission="can_access_tickets"
              exact
            />

            <PrivateRoute
              path="/reviews/"
              component={Reviews}
              requiredPermission="can_access_reviews"
              exact
            />
            {/* <PrivateRoute path='/reviews/r/:id/' component={ReviewReport} exact /> */}
            <PrivateRoute
              path="/reviews/manage/"
              component={AccountsManage}
              requiredPermission="can_access_reviews"
              exact
            />
            <PrivateRoute
              path="/reviews/verification/"
              component={ReviewVerification}
              requiredPermission="can_access_reviews"
              exact
            />
            <PrivateRoute path="/voucher-upload/" component={VoucherUpload} exact />
            <PrivateRoute path="/meta-leads/" component={MetaLeads} staffOnly exact />
            <PrivateRoute path="/login-info/" component={LoginInfo} exact />

            {/* members */}
            <PrivateRoute path="/members/" component={Members} exact />
            <PrivateRoute
              path="/members/add-member"
              component={AddMember}
              exact
            />
            <PrivateRoute
              path="/members/redeem-coupon"
              component={ReedemCoupon}
              exact
            />

            <PrivateRoute
              path="/members/d/:id/"
              component={MemberDetails}
              exact
            />
            <PrivateRoute
              path="/members/edit-details/:id/"
              component={EditMemberDetails}
              exact
            />
            <Route component={NotFound} exact />
          </Switch>
        </Suspense>
      </Router>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    myTeamPermissions: state.teamMembers.myPermissions,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    loadUser: () => dispatch(auth.loadUser()),
    fetchMyTeamPermissions: () => dispatch(teamMembersActions.fetchMyTeamPermissions()),
    fetchStoreConfig: () => dispatch(auth.fetchStoreConfig()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Routes);
